import {I18n} from 'aws-amplify/utils';
import {Alert} from '@mantine/core';

import {SimplePageContentWrapper} from '@flexinet/ui-components';

import {IndexPageHeader} from './IndexPageHeader';
import {DnsRecordsTable} from '../tables/DnsRecordsTable';
import {useZoneNameQuery} from '../hooks/useDnsRecordsQuery';

// FNP-1291 — THE RECORDS SURFACE, rendered identically in two places.
//
// ⚠️ WHY THIS COMPONENT EXISTS. The zone detail page must show a zone's records
// in its wide column rather than a button that navigates to them (the owner's
// pattern: the right-hand side holds the record's main content). The standalone
// `/zones/:zoneId/records` route stays — it is registered, deep-linkable, and
// FNP-1192's tests target it.
//
// Two surfaces, ONE component. Two hand-maintained copies would drift: a column
// added to the table, a control added to the toolbar, or a capability check
// changed would reach one and not the other. `registrars/pages/EditPage.tsx`
// established this shape by embedding the whole `domain-names` ListPage rather
// than its bare table.
//
// ⚠️ WHAT IS DELIBERATELY *NOT* IN HERE: the breadcrumb. `IndexPageNavigation`
// renders Home → Zones → <zone> → Records, which is the trail back for a page
// the user navigated TO. Inside the zone detail page the user is already on the
// zone, under that page's own breadcrumb and title — a second trail ending in
// the zone they are looking at is chrome repeating itself. The standalone page
// keeps its navigation by rendering it as `SimplePageLayout`'s `pageNavigation`
// prop, outside this component.
//
// The header IS in here, and that is the whole point of FNP-1291: Add, Delete,
// Filter and Reload live in `IndexPageHeader`, so a bare table in the detail
// page would show records with no way to add one. Inlining the module's surface
// is what the layout was built for.
export const RecordsSurface = (
    {
        connectionId,
        zoneId,
    }: {
        connectionId: string,
        zoneId: string,
    },
) => {

    const {data: zoneName} = useZoneNameQuery(connectionId, zoneId);

    return (
        <SimplePageContentWrapper>
            <IndexPageHeader
                connectionId={connectionId}
                zoneId={zoneId}
                zoneName={zoneName ?? undefined}
            />
            <DnsRecordsTable
                connectionId={connectionId}
                zoneId={zoneId}
                zoneName={zoneName ?? undefined}
            />
        </SimplePageContentWrapper>
    );
};

// ⚠️ THE MISSING-CONNECTION GUARD, shared for the same reason the surface is.
//
// `listDnsRecords(connectionId: ID!, zoneId: ID!)` needs both, and neither
// caller can always supply the connection: the standalone route carries it in
// the query string, and the detail page resolves it from the query string or
// its list cache. Both can come up empty, and both must say so rather than
// firing a query guaranteed to fail schema validation.
//
// ⚠️ THE GUARD COMES BEFORE ANY QUERY HOOK, and the ordering is load-bearing
// rather than stylistic — mounting React Query machinery purely to render a
// message that needs none of it also breaks in a context with no
// QueryClientProvider (the routing tests are exactly that).
export const RecordsSurfaceWithGuard = (
    {
        connectionId,
        zoneId,
        message,
    }: {
        connectionId?: string,
        zoneId: string,
        // The two callers word this differently: the standalone page is
        // explaining a link that arrived incomplete, the detail page is
        // explaining a zone it could not fully address.
        message: string,
    },
) => {

    if (!connectionId) {
        return (
            <Alert color={'yellow'} title={I18n.get('Cannot show these records')}>
                {message}
            </Alert>
        );
    }

    return <RecordsSurface connectionId={connectionId} zoneId={zoneId}/>;
};
